import React, { useState } from 'react';
import { Copy, CheckCircle, DownloadSimple, FloppyDisk, Spinner, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { creationsAPI } from '@/utils/api';
import PDFViewer from './PDFViewer';
import { downloadBlob } from '@/utils/downloadBlob';

/**
 * OutputActions - Reusable component for Copy, Download PDF, View PDF, Save to Creations
 * 
 * Props:
 * - content: string (text to copy)
 * - pdfType: string (business_plan, offer, sales_page, content_pack, email_sequence, weekly_plan, link_in_bio)
 * - pdfData: object (data to pass to PDF generator)
 * - saveType: string (type for saving to creations)
 * - saveTitle: string (title for saving)
 * - saveContent: object (content data for saving)
 * - onSaved: function (callback after saving)
 * - showCopy: boolean (default true)
 * - showDownload: boolean (default true)
 * - showView: boolean (default true) - NEW: show View PDF button
 * - showSave: boolean (default true)
 * - compact: boolean (smaller buttons)
 */
const OutputActions = ({ 
  content,
  pdfType,
  pdfData,
  saveType,
  saveTitle,
  saveContent,
  onSaved,
  showCopy = true,
  showDownload = true,
  showView = true,
  showSave = true,
  compact = false
}) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerData, setViewerData] = useState({ pdfBase64: null, filename: '' });
  const [loadingViewer, setLoadingViewer] = useState(false);

  const handleCopy = async () => {
    if (!content) return;
    
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const generatePDF = async () => {
    if (!pdfType || !pdfData) {
      toast.error('No data to generate PDF');
      return null;
    }
    
    try {
      const response = await creationsAPI.generatePDF(pdfType, pdfData);
      if (response.data?.pdf_base64) {
        return {
          pdfBase64: response.data.pdf_base64,
          filename: response.data.filename || `PayGBot_${pdfType}.pdf`
        };
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      throw err;
    }
    return null;
  };

  const handleDownload = async () => {
    /* Phase G — V2 grants export to every subscriber */
    setDownloading(true);
    try {
      const result = await generatePDF();
      if (result) {
        // Convert base64 to blob and download
        const byteCharacters = atob(result.pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        // iOS-Safari-safe download (opens in new tab on iOS so user can Share → Save)
        const res = await downloadBlob(blob, result.filename);
        if (res.ios) {
          toast.success("PDF opened — tap Share → Save to Files.", { duration: 6000 });
        } else {
          toast.success('PDF downloaded!');
        }
      }
    } catch (err) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleView = async () => {
    setLoadingViewer(true);
    try {
      const result = await generatePDF();
      if (result) {
        setViewerData(result);
        setViewerOpen(true);
      }
    } catch (err) {
      toast.error('Failed to load PDF');
    } finally {
      setLoadingViewer(false);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    // Keep the data cached for quick re-open
  };

  const handleSave = async () => {
    if (!saveType || !saveTitle || !saveContent) {
      toast.error('Missing save data');
      return;
    }
    
    setSaving(true);
    try {
      await creationsAPI.save(saveType, saveTitle, saveContent);
      setSaved(true);
      toast.success('Saved to My Creations!');
      onSaved?.();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const buttonClass = compact 
    ? 'px-3 py-1.5 text-xs'
    : 'px-4 py-2 text-sm';

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {showCopy && content && (
          <button
            onClick={handleCopy}
            disabled={copied}
            className={`${buttonClass} flex items-center space-x-1.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors disabled:opacity-50`}
            data-testid="output-copy-btn"
          >
            {copied ? <CheckCircle size={16} weight="fill" className="text-emerald-400" /> : <Copy size={16} />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>
        )}
        
        {showView && pdfType && pdfData && (
          <button
            onClick={handleView}
            disabled={loadingViewer}
            className={`${buttonClass} flex items-center space-x-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 font-medium rounded-lg transition-colors disabled:opacity-50`}
            data-testid="output-view-pdf-btn"
          >
            {loadingViewer ? <Spinner size={16} className="animate-spin" /> : <Eye size={16} />}
            <span>{loadingViewer ? 'Loading...' : 'View PDF'}</span>
          </button>
        )}
        
        {showDownload && pdfType && pdfData && (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className={`${buttonClass} flex items-center space-x-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 font-medium rounded-lg transition-colors disabled:opacity-50`}
            data-testid="output-download-btn"
          >
            {downloading ? <Spinner size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
            <span>{downloading ? 'Generating...' : 'Download PDF'}</span>
          </button>
        )}
        
        {showSave && saveType && !saved && (
          <button
            onClick={handleSave}
            disabled={saving}
            className={`${buttonClass} flex items-center space-x-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 font-medium rounded-lg transition-colors disabled:opacity-50`}
            data-testid="output-save-btn"
          >
            {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} />}
            <span>{saving ? 'Saving...' : 'Save to My Creations'}</span>
          </button>
        )}
        
        {showSave && saved && (
          <span className={`${buttonClass} flex items-center space-x-1.5 bg-emerald-500/20 text-emerald-300 font-medium rounded-lg`}>
            <CheckCircle size={16} weight="fill" />
            <span>Saved!</span>
          </span>
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewer
        isOpen={viewerOpen}
        onClose={handleCloseViewer}
        pdfBase64={viewerData.pdfBase64}
        filename={viewerData.filename}
        title={saveTitle || 'Document'}
      />
    </>
  );
};

export default OutputActions;
